import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Theme, CircularProgress, Radio } from '@mui/material';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ReactComponent as LogoIcon } from '../LandingView/assets/logo.svg';
import FormTextField from '../../components/formFields/FormTextField';
import FormSelectField from '../../components/formFields/FormSelectField';
import FormACHField from '../../components/formFields/FormACHfield';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import CommonButton from '../../components/buttons/CommonButton';
import AddressAutocomplete from '../../components/formFields/AddressAutocomplete';
import { VendorOption } from '../../components/VendorConfirmationPopup';
import useSnackBar from '../../hooks/useSnackBar';
import { BANK_ACCOUNT_TYPE_OPTIONS, US_STATES_LIST } from '../../constants/constants';
import { VENDOR_BANKING_AUTHORIZATION_MESSAGE } from '../../constants/authorizationMessages';
import { AddressPrediction, validateAddressVendor } from '../../api/addressApi/addressApi';
import { addressOptionsQueryVendor, mapAddressResultsVendor } from '../VendorsView/helpers';
import { BankingInfo, BankingFormData } from '../VendorsView/types';
import { VENDOR_CONFIRMATION_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import { getBankingDetails, getAchAccountNumberVendor, confirmBankingDetails, updateBankingDetails, getVendorConsentDocument } from '../../api/vendorApi/vendorAccountsApi';
import { yupResolver } from '@hookform/resolvers/yup';
import { createEditBankingDetailsFormSchema } from '../../helpers/formSchemas/formSchemas';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { AxiosError } from 'axios';
import { ErrorData } from '../../types';
import FormMaskedTextField from '../../components/formFields/FormMaskedTextField';
import VendorConfirmationPopupBanking from '../../components/VendorConfirmationPopupBanking';
import ConsentAuthorizationSection from '../../components/ConsentAuthorizationSection';
import Loader from '../../components/Loader';

const useStyles = makeStyles()((theme: Theme) => ({
  pageBackground: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(to left bottom, rgba(37, 157, 168, 0.19), rgba(37, 157, 168, 0))',
    backgroundColor: '#282B3A',
    padding: '20px',

    '@media (max-width: 640px)': {
      padding: '20px',
    },

    '@media (max-height: 550px)': {
      height: '100%',
      padding: '20px',
    },
  },
  signInLogoContainer: {
    marginBottom: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '& svg': {
      width: '24px',
      height: '24px',
      fill: theme.palette.common.white,
      color: theme.palette.common.white,
    },
  },
  welcomeText: {
    fontSize: '28px',
    fontWeight: 600,
    color: theme.palette.common.white,
    marginBottom: '20px',
    textAlign: 'center',
    fontFamily: 'Poppins',
  },
  formContainer: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    padding: '32px',
    maxWidth: '600px',
    boxSizing: 'border-box',
    borderRadius: '16px',
    width: '100%',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    position: 'relative',

    '@media (max-width: 640px)': {
      padding: '24px',
      maxWidth: '100%',
    },
  },
  editIconButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    color: theme.palette.info.main,

    '&:hover': {
      color: theme.palette.info.light,
    },
  },
  formTitle: {
    fontSize: '17px',
    fontWeight: 300,
    color: theme.palette.common.white,
    marginBottom: '20px',
    fontFamily: 'Poppins',
  },
  formFieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    marginBottom: '32px',
  },
  formDetails: {
    fontSize: '20px',
    fontWeight: 600,
    color: theme.palette.common.white,
    // marginBottom: '5px',
    fontFamily: 'Poppins',
  },
  readOnlySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    marginBottom: '32px',
  },
  readOnlyRow: {
    display: 'flex',
    gap: '16px',

    '@media (max-width: 640px)': {
      flexDirection: 'column',
      gap: '24px',
    },
  },
  readOnlyField: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  readOnlyLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
  },
  readOnlyValue: {
    fontSize: '16px',
    fontWeight: 500,
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    padding: '13.5px 0px',
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '0px',
  },
  formFieldsRow: {
    display: 'flex',
    gap: '16px',

    '@media (max-width: 640px)': {
      flexDirection: 'column',
      gap: '24px',
    },
  },
  formFieldFull: {
    flex: 1,
  },
  buttonsContainer: {
    display: 'flex',
    gap: '16px',
    flexDirection: 'column',
    marginTop: '32px',

    '@media (max-width: 640px)': {
      gap: '12px',
    },
  },
  verifyButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'Inter',
    '&:hover': {
      opacity: 0.8,
    },
    padding: '7px 14px 7px 14px',
    borderRadius: '8px',
    backgroundColor: theme.palette.info.main,
  },
  thankYouOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1300,
  },
  thankYouCardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    textAlign: 'center',
  },
  thankYouCard: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    padding: '48px 32px',
    borderRadius: '16px',
    textAlign: 'center',
    maxWidth: '400px',
    width: '90%',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  thankYouCheckIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: `2px solid ${theme.palette.success.main}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 24px',
    fontSize: '40px',
    color: theme.palette.success.main,
  },
  thankYouTitle: {
    fontSize: '24px',
    fontWeight: 600,
    color: theme.palette.common.white,
    marginBottom: '12px',
    fontFamily: 'Poppins',
  },
  thankYouDescription: {
    fontSize: '14px',
    fontWeight: 400,
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: '24px',
    fontFamily: 'Inter',
    lineHeight: '1.6',
  },
  thankYouButton: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
    border: 'none',
    padding: '12px 32px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Poppins',
    cursor: 'pointer',
    width: '100%',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: theme.palette.info.light,
    },
  },
}));

const ConfirmBankingView = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();
  const params = new URLSearchParams(location.search);
  const token = params.get('token')
  const maskedAccountNumber = '************';
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showThankYouCard, setShowThankYouCard] = useState(false);
  const [showAlreadyConfirmedModal, setShowAlreadyConfirmedModal] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [changedFields, setChangedFields] = useState<Partial<BankingFormData>>({});

  // Account number visibility states - separate for edit mode and read-only mode
  const [isAccountNumberVisibleInEdit, setIsAccountNumberVisibleInEdit] = useState(false);
  const [isAccountNumberVisibleInReadOnly, setIsAccountNumberVisibleInReadOnly] = useState(false);
  const [fullAccountNumber, setFullAccountNumber] = useState<string | null>(null);
  const [isAccountNumberLoading, setIsAccountNumberLoading] = useState(false);

  // Address verification mailingState flags
  const [isMailingAddressVerified, setMailingAddressVerified] = useState(false);
  const [isMailingAddressKeptAsEntered, setMailingAddressKeptAsEntered] = useState<boolean | null>(null);
  const [mailingAddressChanged, setMailingAddressChanged] = useState(false);
  const [isUpdatingFromAutocomplete, setIsUpdatingFromAutocomplete] = useState(false);
  const [showAddressVerification, setShowAddressVerification] = useState(false);
  const [initialAddressState, setInitialAddressState] = useState({
    mailingAddress1: '',
    mailingAddress2: '',
    mailingCity: '',
    mailingState: '',
    mailingZipCode: '',
    mailingAddressKeptAsEntered: false,
    mailingAddressVerified: false,
  });
  const [currentMailingBaseline, setCurrentMailingBaseline] = useState({
    mailingAddress1: '',
    mailingAddress2: '',
    mailingCity: '',
    mailingState: '',
    mailingZipCode: '',
    mailingAddressKeptAsEntered: false,
    mailingAddressVerified: false,
  });


  // Participation address verification flags
  const [isParticipationAddressVerified, setParticipationAddressVerified] = useState(false);
  // Track if participation address has been modified from current baseline
  const [participationAddressChanged, setParticipationAddressChanged] = useState(false);
  const [isParticipationAddressKeptAsEntered, setParticipationAddressKeptAsEntered] = useState(false);

  const [showParticipationAddressVerification, setShowParticipationAddressVerification] = useState(false);
  const [initialParticipationAddressState, setInitialParticipationAddressState] = useState({
    participationAddress1: '',
    participationAddress2: '',
    participationCity: '',
    participationState: '',
    participationZip: '',
    participationAddressKeptAsEntered: false,
    participationAddressVerified: false,
  });
  // Track current baseline (updated after each verification) for detecting subsequent changes
  const [currentParticipationBaseline, setCurrentParticipationBaseline] = useState({
    participationAddress1: '',
    participationAddress2: '',
    participationCity: '',
    participationState: '',
    participationZip: '',
    participationAddressKeptAsEntered: false,
    participationAddressVerified: false,
  });
  const [hasAccountNumber, setHasACcountNumber] = useState(false);

  const [data, setData] = useState<BankingInfo | null>(null);
  const [initialData, setInitialData] = useState<BankingFormData | null>(null);
  const [isChangesSaved, setIsChangesSaved] = useState(false);

  // Track whether account details or address details sections have any content
  // const [isAccountDetailsActive, setIsAccountDetailsActive] = useState(false);
  // const [isAddressDetailsActive, setIsAddressDetailsActive] = useState(false);
  const [isParticipationAddressActive, setIsParticipationAddressActive] = useState(false);

  // Track if any ACH or CHECK fields have been entered to make all mandatory
  const [hasAnyAchFieldEntered, setHasAnyAchFieldEntered] = useState(false);
  const [hasAnyCheckFieldEntered, setHasAnyCheckFieldEntered] = useState(false);

  // Consent & Authorization state
  const [isConsentAgreed, setIsConsentAgreed] = useState(false);
  const [hasConfirmedBefore, setHasConfirmedBefore] = useState(false);
  const [consentDocPath, setConsentDocPath] = useState<string | null>(null);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(createEditBankingDetailsFormSchema(hasAnyAchFieldEntered, hasAnyCheckFieldEntered)),
    defaultValues: VENDOR_CONFIRMATION_FORM_DEFAULT_VALUES,
  });

  // Watch all form values for change comparison (must be after formMethods is defined)
  const allValues = useWatch({ control: formMethods.control });

  const {
    formState: { isValid, isDirty },
    handleSubmit,
    reset,
    setValue,
    watch,
    clearErrors,
    trigger
  } = formMethods;

  const formValues = watch() as BankingFormData;
  const name = watch('name');
  const accountType = watch('accountType');
  const accountNumber = watch('accountNumber');
  const routingNumber = watch('routingNumber');
  const mailingAddress1 = watch('mailingAddress1');
  const mailingAddress2 = watch('mailingAddress2');
  const mailingCity = watch('mailingCity');
  const mailingState = watch('mailingState');
  const mailingZipCode = watch('mailingZipCode');
  const participationAddress1 = watch('participationAddress1');
  const participationAddress2 = watch('participationAddress2');
  const participationCity = watch('participationCity');
  const participationState = watch('participationState');
  const participationZip = watch('participationZip');
  const phoneNumber = watch('phoneNumber');
  const defaultPaymentMethod = watch('defaultPaymentMethod');

  // Determine if fields are mandatory based on validation schema logic
  const isAchFieldsMandatory = defaultPaymentMethod === 'ACH' || hasAnyAchFieldEntered;
  const isCheckFieldsMandatory = defaultPaymentMethod === 'CHECK' || hasAnyCheckFieldEntered;

  // Track if any ACH fields have been entered
  useEffect(() => {
    const hasAnyAch = !!(accountType?.trim?.() || accountNumber?.trim?.() || routingNumber?.trim?.());
    setHasAnyAchFieldEntered(hasAnyAch);
  }, [accountType, accountNumber, routingNumber]);

  // Track if any CHECK fields have been entered (excluding mailingAddress2)
  useEffect(() => {
    const hasAnyCheck = !!(mailingAddress1?.trim?.() || mailingCity?.trim?.() || mailingState?.trim?.() || mailingZipCode?.trim?.() || phoneNumber?.trim?.());
    setHasAnyCheckFieldEntered(hasAnyCheck);
  }, [mailingAddress1, mailingCity, mailingState, mailingZipCode, phoneNumber]);

  // Clear validation errors for fields that are no longer mandatory when payment method changes
  useEffect(() => {
    if (defaultPaymentMethod === 'ACH') {
      // Clear CHECK field errors when switching to ACH
      clearErrors(['mailingAddress1', 'mailingAddress2', 'mailingCity', 'mailingState', 'mailingZipCode', 'phoneNumber']);
    } else if (defaultPaymentMethod === 'CHECK') {
      // Clear ACH field errors when switching to CHECK
      clearErrors(['accountType', 'accountNumber', 'routingNumber']);
    }
  }, [defaultPaymentMethod, clearErrors]);

  // A saved vendor address is "kept as entered" only if it is unverified AND actually has data.
  // An empty address has no verification status (prevents a flash of "not verified" on first keystroke).
  const isSavedParticipationAddressKeptAsEntered = (info?: BankingInfo | null) =>
    !!info &&
    info.isparticipationAddressVerified !== true &&
    [
      info.participationAddress1,
      info.participationAddress2,
      info.participationCity,
      info.participationState,
      info.participationZip,
    ].some((value) => (value || '').trim() !== '');

  useEffect(() => {
    getBankingDetails(token as string)
      .then((response) => {
        const data: BankingInfo = response.data;
        setData(data);

        // Handle consentRequired field from API
        if (data.consentRequired) {
          setHasConfirmedBefore(false);
        } else {
          setHasConfirmedBefore(true);
        }

        // Handle consentDocPath field from API
        if (data.consentDocPath) {
          setConsentDocPath(data.consentDocPath);
        }

        // Set initial address verification state from API
        const initialAddress = {
          mailingAddress1: data.mailing.address1 || '',
          mailingAddress2: data.mailing.address2 || '',
          mailingCity: data.mailing.city || '',
          mailingState: data.mailing.state || '',
          mailingZipCode: data.mailing.zip || '',
          mailingAddressKeptAsEntered: data.mailing.isAddressVerified === false,
          mailingAddressVerified: data.mailing.isAddressVerified === true,
        };
        setInitialAddressState(initialAddress);

        // Set initial participation address verification state from API
        const initialParticipationAddress = {
          participationAddress1: data.participationAddress1 || '',
          participationAddress2: data.participationAddress2 || '',
          participationCity: data.participationCity || '',
          participationState: data.participationState || '',
          participationZip: data.participationZip || '',
          participationAddressKeptAsEntered: isSavedParticipationAddressKeptAsEntered(data),
          participationAddressVerified: data.isparticipationAddressVerified === true,
        };
        setInitialParticipationAddressState(initialParticipationAddress);

        // If address is verified by admin, set the flag
        if (data.mailing.isAddressVerified) {
          setMailingAddressVerified(true);
        } else if (data.mailing.isAddressVerified === false) {
          setMailingAddressKeptAsEntered(true);
        }

        // If participation address is verified by admin, set the flag
        if (data.isparticipationAddressVerified) {
          setParticipationAddressVerified(true);
        }
        else {
          setParticipationAddressKeptAsEntered(isSavedParticipationAddressKeptAsEntered(data));
        }

        if (data.bank.hasAccountNumber) {
          setHasACcountNumber(true);
        }

        const initialForm: BankingFormData = {
          name: data.name || '',
          accountType: data.bank.accountType || '',
          accountNumber: data.bank.hasAccountNumber ? '************' : '',
          routingNumber: data.bank.routingNumber || '',
          mailingAddress1: data.mailing.address1 || '',
          mailingAddress2: data.mailing.address2 || '',
          mailingCity: data.mailing.city || '',
          mailingState: data.mailing.state || '',
          mailingZipCode: data.mailing.zip || '',
          isAddressVerified: data.mailing.isAddressVerified || false,
          phoneNumber: data.mailing.phoneNumber || '',
          email: data.email || '',
          participationAddress1: data.participationAddress1 || '',
          participationAddress2: data.participationAddress2 || '',
          participationCity: data.participationCity || '',
          participationState: data.participationState || '',
          participationZip: data.participationZip || '',
          vendorType: data.vendorType || '',
          defaultPaymentMethod: data.defaultPaymentMethod || '',
          isparticipationAddressVerified: data.isparticipationAddressVerified || false,
          contactName: data.contactName || '',
        };
        reset(initialForm);
        setInitialData(initialForm);
        setIsDataLoading(false);
      })
      .catch((error: AxiosError<ErrorData>) => {
        setIsDataLoading(false);
        // Check if the error is due to token already being used
        if (error.response?.data.message === 'Failed to retrieve vendor confirmation details. Token has already been used') {
          setShowAlreadyConfirmedModal(true);
        } else {
          setIsError(true);
          setAlert((prev) => ({
            ...prev,
            message: error.response?.data.message || 'Failed to fetch banking details.',
            type: 'error',
          }));
        }
      });
  }, [reset, setAlert]);

  const getAccountNumber = async () => {
    if (isAccountNumberLoading) return;

    const isVisible = isEditing ? isAccountNumberVisibleInEdit : isAccountNumberVisibleInReadOnly;
    const setVisibility = isEditing ? setIsAccountNumberVisibleInEdit : setIsAccountNumberVisibleInReadOnly;

    // If no account number entered, just toggle visibility
    if (!hasAccountNumber) {
      setVisibility(!isVisible);
      return;
    }
    // If currently visible, just hide it
    if (isVisible) {
      setVisibility(false);
      return;
    }

    // If already fetched once, just toggle without another API call
    if (fullAccountNumber) {
      setVisibility(true);
      return;
    }

    // Otherwise, fetch full account number from API
    setIsAccountNumberLoading(true);
    try {
      const res = await getAchAccountNumberVendor(token as string);
      const fetchedNumber = res.data.accountNumber;
      setFullAccountNumber(fetchedNumber);
      setValue('accountNumber', fetchedNumber);
      setVisibility(true);
    } catch (error) {
      let errorMessage = 'Error fetching account number.';
      const axiosError = error as AxiosError<ErrorData>;
      if (axiosError?.isAxiosError) {
        errorMessage = axiosError.response?.data?.message ?? errorMessage;
      }
      setAlert((prev) => ({
        ...prev,
        message: errorMessage,
        type: 'error',
      }));
    } finally {
      setIsAccountNumberLoading(false);
    }
  };


  // Helper to treat null and empty string as equal for comparison
  const norm = (v: unknown) => (v === null || v === undefined ? '' : v);
  const hasChanged = (a: unknown, b: unknown) => norm(a) !== norm(b);

  const onSubmitForm = () => {
    // Update fullAccountNumber if the account number is not masked
    if (accountNumber && accountNumber !== '************') {
      setFullAccountNumber(accountNumber);
    }

    // Build changedFields object by comparing with initialData
    const changedFields: Partial<BankingFormData> = {};
    if (initialData) {
      if (hasChanged(initialData.name, allValues.name)) changedFields.name = allValues.name;
      if (hasChanged(initialData.defaultPaymentMethod, allValues.defaultPaymentMethod)) changedFields.defaultPaymentMethod = allValues.defaultPaymentMethod;
      if (hasChanged(initialData.accountType, allValues.accountType)) changedFields.accountType = allValues.accountType;
      if (hasChanged(initialData.accountNumber, allValues.accountNumber)) changedFields.accountNumber = allValues.accountNumber;
      if (hasChanged(initialData.routingNumber, allValues.routingNumber)) changedFields.routingNumber = allValues.routingNumber;
      if (hasChanged(initialData.mailingAddress1, allValues.mailingAddress1)) changedFields.mailingAddress1 = allValues.mailingAddress1;
      if (hasChanged(initialData.mailingAddress2, allValues.mailingAddress2)) changedFields.mailingAddress2 = allValues.mailingAddress2;
      if (hasChanged(initialData.mailingCity, allValues.mailingCity)) changedFields.mailingCity = allValues.mailingCity;
      if (hasChanged(initialData.mailingState, allValues.mailingState)) changedFields.mailingState = allValues.mailingState;
      if (hasChanged(initialData.mailingZipCode, allValues.mailingZipCode)) changedFields.mailingZipCode = allValues.mailingZipCode;
      if (hasChanged(initialData.phoneNumber, allValues.phoneNumber)) changedFields.phoneNumber = allValues.phoneNumber;
      // if (hasChanged(initialData.isAddressVerified, allValues['isAddressVerified'])) changedFields.isAddressVerified = allValues['isAddressVerified'];
      if (hasChanged(initialData.email, allValues.email)) changedFields.email = allValues.email;
      if (hasChanged(initialData.participationAddress1, allValues.participationAddress1)) changedFields.participationAddress1 = allValues.participationAddress1;
      if (hasChanged(initialData.participationAddress2, allValues.participationAddress2)) changedFields.participationAddress2 = allValues.participationAddress2;
      if (hasChanged(initialData.participationCity, allValues.participationCity)) changedFields.participationCity = allValues.participationCity;
      if (hasChanged(initialData.participationState, allValues.participationState)) changedFields.participationState = allValues.participationState;
      if (hasChanged(initialData.participationZip, allValues.participationZip)) changedFields.participationZip = allValues.participationZip;
      if (hasChanged(initialData.contactName, allValues.contactName)) changedFields.contactName = allValues.contactName;
      // if (hasChanged(initialData.isparticipationAddressVerified, allValues['isparticipationAddressVerified'])) changedFields.is
    }


    // Save only if there are changes
    if (Object.keys(changedFields).length > 0) {
      setIsChangesSaved(true);
      setIsEditing(false);
      setChangedFields(changedFields)
    } else {
      setChangedFields({});
      setIsEditing(false);
    }
  };

  const onConfirm = async (data: BankingFormData) => {
    setIsFormSubmitting(true);
    try {

      let accountNumberToSubmit = data.accountNumber;
      if (data.accountNumber === '************' && !fullAccountNumber) {
        try {
          const res = await getAchAccountNumberVendor(token as string);
          accountNumberToSubmit = res.data.accountNumber;
          setFullAccountNumber(accountNumberToSubmit);
        } catch (error) {
          let errorMessage = 'Error fetching account number.';
          const axiosError = error as AxiosError<ErrorData>;
          if (axiosError?.isAxiosError) {
            errorMessage = axiosError.response?.data?.message ?? errorMessage;
          }
          setAlert((prev) => ({
            ...prev,
            message: errorMessage,
            type: 'error',
          }));
        }
      } else if (data.accountNumber === '************' && fullAccountNumber) {
        accountNumberToSubmit = fullAccountNumber;
      }

      // Always include accountNumber and isAddressVerified if they are changed or required
      const dataToSubmit: Partial<BankingFormData> = {
        ...changedFields,
      };
      // If accountNumber was changed or is in changedFields, ensure it's the resolved value
      if (Object.prototype.hasOwnProperty.call(changedFields, 'accountNumber')) {
        dataToSubmit.accountNumber = accountNumberToSubmit;
      }
      // If isAddressVerified was changed or is in changedFields, ensure it's the current value
      if (
        ['mailingAddress1', 'mailingCity', 'mailingState', 'mailingZipCode'].some(field =>
          Object.prototype.hasOwnProperty.call(changedFields, field)
        )
      ) {
        dataToSubmit.isAddressVerified = isMailingAddressVerified;
      }
      if (
        ['participationAddress1', 'participationCity', 'participationState', 'participationZip'].some(field =>
          Object.prototype.hasOwnProperty.call(changedFields, field)
        )
      ) {
        dataToSubmit.isparticipationAddressVerified = isParticipationAddressVerified;
      }

      if (!isChangesSaved) {
        const res = await confirmBankingDetails(token as string);
        setAlert((prev) => ({
          ...prev,
          message: res.data.message || 'Banking details confirmed successfully.',
          type: 'success',
        }));
      } else {
        const res = await updateBankingDetails(token as string, dataToSubmit);
        setAlert((prev) => ({
          ...prev,
          message: res.data.message || 'Banking details updated successfully.',
          type: 'success',
        }));
      }
      setHasConfirmedBefore(true);
      setShowThankYouCard(true);
    } catch (error) {
      let errorMessage = 'Error confirming banking information.';
      const axiosError = error as AxiosError<ErrorData>;
      if (axiosError?.isAxiosError) {
        errorMessage = axiosError.response?.data?.message ?? errorMessage;
      }
      else if (error instanceof Error) {
        errorMessage = error.message;
      }
      setAlert((prev) => ({
        ...prev,
        message: errorMessage,
        type: 'error',
      }));
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();

    // Mailing address verification flags
    setMailingAddressVerified(false);
    setMailingAddressKeptAsEntered(false);

    // Participation address verification flags
    const participationVerified = !!data?.isparticipationAddressVerified;
    setParticipationAddressVerified(participationVerified);
    setParticipationAddressKeptAsEntered(isSavedParticipationAddressKeptAsEntered(data));

    // initially there is no change in participation address
    setParticipationAddressChanged(false);
    setMailingAddressChanged(false);

    // Ensure the UI shows the correct verification message after cancel
    // (force update by resetting the initial state objects)
    setInitialAddressState({
      mailingAddress1: data?.mailing.address1 || '',
      mailingAddress2: data?.mailing.address2 || '',
      mailingCity: data?.mailing.city || '',
      mailingState: data?.mailing.state || '',
      mailingZipCode: data?.mailing.zip || '',
      mailingAddressKeptAsEntered: data?.mailing.isAddressVerified === false,
      mailingAddressVerified: data?.mailing.isAddressVerified === true,
    });
    setInitialParticipationAddressState({
      participationAddress1: data?.participationAddress1 || '',
      participationAddress2: data?.participationAddress2 || '',
      participationCity: data?.participationCity || '',
      participationState: data?.participationState || '',
      participationZip: data?.participationZip || '',
      participationAddressKeptAsEntered: isSavedParticipationAddressKeptAsEntered(data),
      participationAddressVerified: data?.isparticipationAddressVerified === true,
    });
  };

  const handleVerifyAddress = async () => {
    if (!mailingAddress1) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please enter an address to verify.',
        type: 'warning',
      }));
      return;
    }
    setShowAddressVerification(true);
  };

  const handleVerifyParticipationAddress = async () => {
    if (!participationAddress1) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please enter a participation address to verify.',
        type: 'warning',
      }));
      return;
    }
    setShowParticipationAddressVerification(true);
  };

  const handleAddressSelect = async (address: AddressPrediction) => {
    if (!address) return;

    const addressText = address?.description || '';
    const selectedAddressLine1 = address?.placeId
      ? address?.structuredFormatting?.mainText?.trim() || addressText.trim()
      : addressText;

    if (addressText) {
      setValue('mailingAddress1', selectedAddressLine1);
      if (address?.placeId) {
        setMailingAddressVerified(true);
        setMailingAddressChanged(false);
        setMailingAddressKeptAsEntered(false);
      }

      if (address?.placeId) {
        try {
          setIsUpdatingFromAutocomplete(true);
          const validatedAddress = await validateAddressVendor(addressText, token as string, address?.placeId);

          if (validatedAddress.data) {
            const { addressLine1, addressLine2, city, state, zip } = validatedAddress.data;
            const resolvedAddressLine1 =
              (addressLine1 && addressLine1.length >= selectedAddressLine1.length
                ? addressLine1
                : selectedAddressLine1) ||
              addressLine1 ||
              selectedAddressLine1;

            setValue('mailingAddress1', resolvedAddressLine1);
            if (addressLine2) setValue('mailingAddress2', addressLine2);
            if (city) setValue('mailingCity', city);
            if (state) setValue('mailingState', state);
            if (zip) setValue('mailingZipCode', zip);

            // Update initial address state to match the verified address
            // This prevents the first useEffect from immediately resetting the verification flag
            setInitialAddressState({
              mailingAddress1: resolvedAddressLine1 || '',
              mailingAddress2: addressLine2 || '',
              mailingCity: city || '',
              mailingState: state || '',
              mailingZipCode: zip || '',
              mailingAddressKeptAsEntered: false,
              mailingAddressVerified: true,
            });
            setCurrentMailingBaseline({
              mailingAddress1: resolvedAddressLine1 || '',
              mailingAddress2: addressLine2 || '',
              mailingCity: city || '',
              mailingState: state || '',
              mailingZipCode: zip || '',
              mailingAddressKeptAsEntered: false,
              mailingAddressVerified: true
            });

            // Set address verified by admin flag to show success message
            clearErrors(['mailingAddress1', 'mailingAddress2', 'mailingCity', 'mailingState', 'mailingZipCode']);

            trigger(['mailingAddress1', 'mailingAddress2', 'mailingCity', 'mailingState', 'mailingZipCode']);
          }
          setTimeout(() => setIsUpdatingFromAutocomplete(false), 100);
        } catch (error) {
          setValue('mailingAddress1', selectedAddressLine1 || addressText);
          setIsUpdatingFromAutocomplete(false);
        }
      }
    }
  };

  const handleParticipationAddressSelect = async (address: AddressPrediction | null) => {
    const addressText = address?.description || '';

    // Cleared (clear button or deleting the last character): store '' so the field can be emptied
    if (!addressText.trim()) {
      setValue('participationAddress1', '', { shouldDirty: true, shouldValidate: true });
      setParticipationAddressVerified(false);
      return;
    }

    // Manual typing: just store the text. The change-detection effect decides the
    // verification status, so a typed (possibly partial) address is never marked verified.
    if (!address?.placeId) {
      setValue('participationAddress1', addressText, { shouldDirty: true, shouldValidate: true });
      setParticipationAddressVerified(false);
      return;
    }

    const selectedAddressLine1 =
      address?.structuredFormatting?.mainText?.trim() || addressText.trim();

    setValue('participationAddress1', selectedAddressLine1);
    setCurrentParticipationBaseline({
      participationAddress1: selectedAddressLine1 || '',
      participationAddress2: '',
      participationCity: '',
      participationState: '',
      participationZip: '',
      participationAddressKeptAsEntered: false,
      participationAddressVerified: true
    });
    setParticipationAddressChanged(false);

    try {
      const validatedAddress = await validateAddressVendor(addressText, token as string, address?.placeId);

      if (validatedAddress.data) {
        const { addressLine1, addressLine2, city, state, zip } = validatedAddress.data;
        const resolvedAddressLine1 =
          (addressLine1 && addressLine1.length >= selectedAddressLine1.length
            ? addressLine1
            : selectedAddressLine1) ||
          addressLine1 ||
          selectedAddressLine1;

        setValue('participationAddress1', resolvedAddressLine1);
        if (addressLine2) setValue('participationAddress2', addressLine2);
        if (city) setValue('participationCity', city);
        if (state) setValue('participationState', state);
        if (zip) setValue('participationZip', zip);

        setCurrentParticipationBaseline({
          participationAddress1: resolvedAddressLine1 || '',
          participationAddress2: addressLine2 || '',
          participationCity: city || '',
          participationState: state || '',
          participationZip: zip || '',
          participationAddressKeptAsEntered: false,
          participationAddressVerified: true
        });

        // Set address verified by admin flag to show success message
        setParticipationAddressVerified(true);
        clearErrors([
          'participationAddress1',
          'participationAddress2',
          'participationCity',
          'participationState',
          'participationZip'
        ]);

        trigger([
          'participationAddress1',
          'participationAddress2',
          'participationCity',
          'participationState',
          'participationZip'
        ]);
      }
    } catch (error) {
      // Validation failed, so the address is NOT verified
      setValue('participationAddress1', selectedAddressLine1 || addressText);
      setCurrentParticipationBaseline({
        participationAddress1: selectedAddressLine1 || addressText,
        participationAddress2: '',
        participationCity: '',
        participationState: '',
        participationZip: '',
        participationAddressKeptAsEntered: false,
        participationAddressVerified: false,
      });
      setParticipationAddressVerified(false);
    }
  };

  const handleVendorConfirmation = (selectedVendor?: VendorOption, keepAsEntered?: boolean) => {
    setShowAddressVerification(false);

    if (keepAsEntered) {
      setCurrentMailingBaseline({
        mailingAddress1: mailingAddress1 || '',
        mailingAddress2: mailingAddress2 || '',
        mailingCity: mailingCity || '',
        mailingState: mailingState || '',
        mailingZipCode: mailingZipCode || '',
        mailingAddressKeptAsEntered: true,
        mailingAddressVerified: false
      });
      setMailingAddressKeptAsEntered(true);
      setMailingAddressVerified(false);
      setMailingAddressChanged(false);
      setValue('mailingAddress1', mailingAddress1, { shouldDirty: true });
    }
  };

  const handleVendorOptionSelect = async (vendor: VendorOption) => {
    const addressForValidation: AddressPrediction = {
      description: vendor.address,
      placeId: vendor.placeId,
      structuredFormatting: {
        mainText: vendor.address,
        secondaryText: '',
      },
    };

    setMailingAddressVerified(true);
    setMailingAddressKeptAsEntered(false);
    setShowAddressVerification(false);
    await handleAddressSelect(addressForValidation);
  };

  const handleParticipationVendorConfirmation = (selectedVendor?: VendorOption, keepAsEntered?: boolean) => {
    setShowParticipationAddressVerification(false);

    if (keepAsEntered) {
      setParticipationAddressKeptAsEntered(true);
      setCurrentParticipationBaseline({
        participationAddress1: participationAddress1 || '',
        participationAddress2: participationAddress2 || '',
        participationCity: participationCity || '',
        participationState: participationState || '',
        participationZip: participationZip || '',
        participationAddressKeptAsEntered: true,
        participationAddressVerified: false,
      });
      setParticipationAddressChanged(false);
      setParticipationAddressVerified(false);
      setValue('participationAddress1', participationAddress1, { shouldDirty: true });
    }
  };

  const handleParticipationVendorOptionSelect = async (vendor: VendorOption) => {
    const addressForValidation: AddressPrediction = {
      description: vendor.address,
      placeId: vendor.placeId,
      structuredFormatting: {
        mainText: vendor.address,
        secondaryText: '',
      },
    };

    setParticipationAddressVerified(true);
    setParticipationAddressKeptAsEntered(false);
    setShowParticipationAddressVerification(false);
    await handleParticipationAddressSelect(addressForValidation);
  };

  // Track address changes and reset verification flags

  useEffect(() => {
    // Skip change detection if we're currently updating from autocomplete
    if (isUpdatingFromAutocomplete) return;

    const isMailingRestored =
      mailingAddress1 === initialAddressState.mailingAddress1 &&
      mailingAddress2 === initialAddressState.mailingAddress2 &&
      mailingCity === initialAddressState.mailingCity &&
      mailingState === initialAddressState.mailingState &&
      mailingZipCode === initialAddressState.mailingZipCode;

    const hasChangedFromBaseline = (
      mailingAddress1 !== currentMailingBaseline.mailingAddress1 ||
      mailingAddress2 !== currentMailingBaseline.mailingAddress2 ||
      mailingCity !== currentMailingBaseline.mailingCity ||
      mailingState !== currentMailingBaseline.mailingState ||
      mailingZipCode !== currentMailingBaseline.mailingZipCode
    );
    // If back to original API address, restore original verification state
    if (isMailingRestored) {
      setMailingAddressChanged(false);
      if (initialAddressState.mailingAddressVerified) {
        setMailingAddressVerified(true);
        setMailingAddressKeptAsEntered(false);
      } else if (initialAddressState.mailingAddressKeptAsEntered) {
        setMailingAddressKeptAsEntered(true);
        setMailingAddressVerified(false);
      }
    }
    else if (hasChangedFromBaseline) {
      // Address has changed from baseline - reset verification and show verify button
      if (!mailingAddressChanged) {
        setMailingAddressChanged(true);
        setMailingAddressVerified(false);
        setMailingAddressKeptAsEntered(false);
      }
    } else {
      // Address has changed from initial - reset verification and show verify button
      setMailingAddressVerified(currentMailingBaseline.mailingAddressVerified);
      setMailingAddressKeptAsEntered(currentMailingBaseline.mailingAddressKeptAsEntered);
      setMailingAddressChanged(false);
    }
  }, [mailingAddress1, mailingAddress2, mailingCity, mailingState, mailingZipCode, initialAddressState, isMailingAddressVerified, isMailingAddressKeptAsEntered, mailingAddressChanged, isUpdatingFromAutocomplete]);

  // Track participation address changes and reset verification flags

  // Detect changes to participation address and reset verification if modified
  useEffect(() => {
    // Skip change detection if we're currently updating from autocomplete
    if (isUpdatingFromAutocomplete) return;

    const isParticipationRestored =
      participationAddress1 === initialParticipationAddressState.participationAddress1 &&
      participationCity === initialParticipationAddressState.participationCity &&
      participationState === initialParticipationAddressState.participationState &&
      participationZip === initialParticipationAddressState.participationZip;

    const hasChangedFromBaseline = (
      participationAddress1 !== currentParticipationBaseline.participationAddress1 ||
      participationAddress2 !== currentParticipationBaseline.participationAddress2 ||
      participationCity !== currentParticipationBaseline.participationCity ||
      participationState !== currentParticipationBaseline.participationState ||
      participationZip !== currentParticipationBaseline.participationZip
    );
    // If back to original API address, restore original verification state
    if (isParticipationRestored) {
      setParticipationAddressChanged(false);
      if (initialParticipationAddressState.participationAddressVerified) {
        setParticipationAddressVerified(true);
        setParticipationAddressKeptAsEntered(false);
      } else if (initialParticipationAddressState.participationAddressKeptAsEntered) {
        setParticipationAddressKeptAsEntered(true);
        setParticipationAddressVerified(false);
      }
    }
    else if (hasChangedFromBaseline) {
      // Address has changed from baseline - reset verification and show verify button
      if (!participationAddressChanged) {
        setParticipationAddressChanged(true);
        setParticipationAddressVerified(false);
        setParticipationAddressKeptAsEntered(false);
      }
    } else {
      // Address has changed from initial - reset verification and show verify button
      setParticipationAddressVerified(currentParticipationBaseline.participationAddressVerified);
      setParticipationAddressKeptAsEntered(currentParticipationBaseline.participationAddressKeptAsEntered);
      setParticipationAddressChanged(false);
    }
  }, [participationAddress1, participationAddress2, participationCity, participationState, participationZip, initialParticipationAddressState, isParticipationAddressVerified, isParticipationAddressKeptAsEntered, participationAddressChanged, isUpdatingFromAutocomplete]);

  // Helper function to check if a field has meaningful content
  const hasContent = (value: unknown) => {
    if (!value) return false;
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      if (obj.id || obj.label) {
        const id = obj.id as string | undefined;
        const label = obj.label as string | undefined;
        return Boolean(id?.trim() || label?.trim());
      }
      return false;
    }
    return String(value).trim() !== '';
  };

  // Track when account details section has any content
  // useEffect(() => {
  //   const hasAccountData = hasContent(accountType) || hasContent(accountNumber) || hasContent(routingNumber);
  //   // setIsAccountDetailsActive(hasAccountData);
  // }, [accountType, accountNumber, routingNumber]);

  // Track when address details section has any content
  // useEffect(() => {
  //   const hasAddressData = hasContent(mailingAddress1) ||
  //     hasContent(mailingCity) ||
  //     hasContent(mailingState) ||
  //     hasContent(mailingZipCode);
  //   // setIsAddressDetailsActive(hasAddressData);
  // }, [mailingAddress1, mailingCity, mailingState, mailingZipCode]);

  // Track when participation address details section has any content
  useEffect(() => {
    const hasParticipationAddressData = hasContent(participationAddress1) ||
      hasContent(participationAddress2) ||
      hasContent(participationCity) ||
      hasContent(participationState) ||
      hasContent(participationZip);
    setIsParticipationAddressActive(hasParticipationAddressData);
  },[participationAddress1, participationAddress2, participationCity, participationState, participationZip]);

  const getAccountTypeLabel = (typeId: string) => {
    return BANK_ACCOUNT_TYPE_OPTIONS.find(opt => opt.id === typeId)?.label || typeId;
  };

  const getStateLabel = (stateId: string) => {
    return US_STATES_LIST.find(opt => opt.id === stateId)?.label || stateId;
  };

  // Helper variables for address verification logic
  const isMailingAddressChanged =
    mailingAddress1 !== initialAddressState.mailingAddress1 ||
    mailingCity !== initialAddressState.mailingCity ||
    mailingState !== initialAddressState.mailingState ||
    mailingZipCode !== initialAddressState.mailingZipCode;

  const isMailingAddressNeedsVerification =
    isMailingAddressChanged && !isMailingAddressVerified && !isMailingAddressKeptAsEntered;

  const isParticipationAddressChanged =
    participationAddress1 !== initialParticipationAddressState.participationAddress1 ||
    participationCity !== initialParticipationAddressState.participationCity ||
    participationState !== initialParticipationAddressState.participationState ||
    participationZip !== initialParticipationAddressState.participationZip;

  // Only a complete vendor address can be verified; empty or partial addresses can be saved as-is
  const isParticipationAddressComplete = [
    participationAddress1,
    participationCity,
    participationState,
    participationZip,
  ].every((value) => (value || '').trim() !== '');
  // An empty vendor address has no verification status to show
  const hasParticipationAddress = [
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
  ].some((value) => (value || '').trim() !== '');

  const isParticipationAddressNeedsVerification =
    isParticipationAddressComplete &&
    isParticipationAddressChanged &&
    !isParticipationAddressVerified &&
    !isParticipationAddressKeptAsEntered;

  const handleViewTermsAndConditions = async () => {
    if (!consentDocPath) {
      setAlert((prev) => ({
        ...prev,
        message: 'Terms & Conditions document not found.',
        type: 'error',
      }));
      return;
    }

    if (!token) {
      setAlert((prev) => ({
        ...prev,
        message: 'Invalid or missing token.',
        type: 'error',
      }));
      return;
    }

    try {
      const encodedDocumentKey = encodeURIComponent(consentDocPath);
      const response = await getVendorConsentDocument(token, encodedDocumentKey);
      if (response.data?.downloadUrl) {
        const fileUrl = response.data.downloadUrl;
        // Open document in new tab for viewing
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = '';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setAlert((prev) => ({
          ...prev,
          message: response.data?.message || 'No file found to view.',
          type: 'info',
        }));
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'Error viewing Terms & Conditions',
        type: 'error',
      }));
    }
  };

  if (isDataLoading) {
    return (
      <Box className={classes.pageBackground}>
        <Box className={classes.signInLogoContainer}>
          <LogoIcon />
        </Box>
        <Box mb="100px">
          <Loader colorType="warning" />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.pageBackground}>
      <Box className={classes.signInLogoContainer}>
        <LogoIcon />
      </Box>

      {!showThankYouCard && !showAlreadyConfirmedModal && (
        <>
          <Typography className={classes.welcomeText}>
            Banking Information
          </Typography>

          <Typography className={classes.formTitle}>
            Review and confirm your banking details for {name}
          </Typography>
        </>
      )}

      <Box className={classes.formContainer}>
        <FormProvider {...formMethods}>
          <form>
            {showAlreadyConfirmedModal ? (
              <Box className={classes.thankYouCardContent}>
                <Box className={classes.thankYouCheckIcon}>
                  ✓
                </Box>
                <Typography className={classes.thankYouTitle}>
                  You have already Confirmed!
                </Typography>
                <Typography className={classes.thankYouDescription}>
                  Looks like you have already submitted your confirmation.
                </Typography>
                <button
                  className={classes.thankYouButton}
                  onClick={() => navigate('/vendors')}
                >
                  Close
                </button>
              </Box>
            ) : showThankYouCard ? (
              // Thank You Card - Replace entire form content
              <Box className={classes.thankYouCardContent}>
                <Box className={classes.thankYouCheckIcon}>
                  ✓
                </Box>
                <Typography className={classes.thankYouTitle}>
                  Thank you
                </Typography>
                <Typography className={classes.thankYouDescription}>
                  Your banking details have been submitted.
                  <br />
                  Admin review is in progress.
                </Typography>
                <button
                  className={classes.thankYouButton}
                  onClick={() => navigate('/vendors')}
                >
                  Close
                </button>
              </Box>
            ) : (
              <>
                {isEditing ? (
                  // Edit Mode - Show Form Fields
                  <Box className={classes.formFieldsContainer}>
                    {/* Account Details Section */}
                    <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                      <Typography className={classes.formDetails}>
                        ACH
                      </Typography>
                      <Box display='flex' alignItems='center' gap='8px'>
                        <Typography sx={{ color: '#ffffff', fontSize: '12px' }}>Default Method</Typography>
                        <Radio
                          checked={formValues.defaultPaymentMethod === 'ACH'}
                          onChange={() => setValue('defaultPaymentMethod', 'ACH', { shouldDirty: true })}
                          sx={{
                            color: '#5e6174',
                            '&.Mui-checked': {
                              color: '#2DD4BF',
                            },
                            padding: '4px',
                          }}
                          size='small'
                        />
                      </Box>
                    </Box>
                    {(() => {
                      const provisioningStatus = data?.bank?.provisioningStatus;
                      const isAchEditable = !data?.bank || provisioningStatus === 'NotStarted' || provisioningStatus === 'Error';

                      return (<>
                        <Box className={classes.formFieldsRow}>
                          <Box className={classes.formFieldFull}>
                            <FormTextField name='name' label='Name' disabled />
                          </Box>

                          <Box className={classes.formFieldFull}>
                            <FormSelectField
                              name='accountType'
                              label={`Account Type${isAchFieldsMandatory ? ' *' : ''}`}
                              options={BANK_ACCOUNT_TYPE_OPTIONS}
                              width='100%'
                              disabled={!isAchEditable}
                              shouldClearable={isAchEditable}
                            />
                          </Box>
                        </Box>

                        <Box className={classes.formFieldsRow}>
                          <Box className={classes.formFieldFull}>
                            <FormACHField
                              name='accountNumber'
                              label={`Account Number${isAchFieldsMandatory ? ' *' : ''}`}
                              isEncrypted
                              isEncryptedValueLoading={isAccountNumberLoading}
                              isValueVisible={isAccountNumberVisibleInEdit}
                              onRightIconClick={() => getAccountNumber()}
                              disabled={!isAchEditable || !isAccountNumberVisibleInEdit}
                            />
                          </Box>
                          <Box className={classes.formFieldFull}>
                            <FormTextField name='routingNumber' label={`Routing No.${isAchFieldsMandatory ? ' *' : ''}`} disabled={!isAchEditable}
                            />
                          </Box>
                        </Box>
                      </>)

                    })()}

                    {/* Address Section */}
                    <Box className={classes.divider} />
                    <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                      <Typography className={classes.formDetails}>
                        CHECK
                      </Typography>
                      <Box display='flex' alignItems='center' gap='8px'>
                        <Typography sx={{ color: '#ffffff', fontSize: '12px' }}>Default Method</Typography>
                        <Radio
                          checked={formValues.defaultPaymentMethod === 'CHECK'}
                          onChange={() => setValue('defaultPaymentMethod', 'CHECK', { shouldDirty: true })}
                          sx={{
                            color: '#5e6174',
                            '&.Mui-checked': {
                              color: '#2DD4BF',
                            },
                            padding: '4px',
                          }}
                          size='small'
                        />
                      </Box>
                    </Box>
                    {(() => {
                      // const isCheckEditable = !data?.mailing || provisioningStatus === 'NotStarted' || provisioningStatus === 'Error';

                      return (<>
                        <Box mt='4px' display='flex' justifyContent='space-between'>

                          {isMailingAddressVerified && !mailingAddressChanged ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#4CAF50',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: 'Inter',
                                padding: '5px'
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: '16px' }} />
                              <span>Address verification completed</span>

                            </Box>
                          ) : isMailingAddressKeptAsEntered && !mailingAddressChanged ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#FF9800',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: 'Inter',
                                padding: '5px'
                              }}
                            >
                              <ErrorIcon sx={{ fontSize: '16px' }} />
                              <span>Address not verified by USPS</span>
                            </Box>

                          ) : (
                            mailingAddress1 && mailingCity && mailingState && mailingZipCode && (
                              <>  <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  color: '#FF9800',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  fontFamily: 'Inter',
                                  padding: '5px'
                                }}
                              >
                                <ErrorIcon sx={{ fontSize: '16px' }} />
                                <span>Address not verified. Please verify</span>
                              </Box>

                                <Box
                                  onClick={handleVerifyAddress}
                                  className={classes.verifyButton}
                                >
                                  <span>Verify</span>
                                </Box>
                              </>
                            )
                          )}
                        </Box>

                        <Box>
                          <AddressAutocomplete
                            name='mailingAddress1'
                            label={`Address Line 1${isCheckFieldsMandatory ? ' *' : ''}`}
                            optionsQuery={addressOptionsQueryVendor}
                            mapQueryResults={mapAddressResultsVendor}
                            onValueChange={handleAddressSelect}
                            isDisableClearable={false}
                            addressType='Banking Address'
                            InputProps={{
                              value: mailingAddress1 || '',
                              placeholder: 'Enter address line 1'
                            }}
                            // disabled={!isCheckEditable}
                          />
                        </Box>

                        <Box>
                          <FormTextField name='mailingAddress2' label='Address Line 2' />
                        </Box>

                        <Box className={classes.formFieldsRow}>
                          <Box className={classes.formFieldFull}>
                            <FormTextField name='mailingCity' label={`City${isCheckFieldsMandatory ? ' *' : ''}`}  />
                          </Box>
                          <Box className={classes.formFieldFull}>
                            <FormSelectField
                              name='mailingState'
                              label={`State${isCheckFieldsMandatory ? ' *' : ''}`}
                              options={US_STATES_LIST}
                              width='100%'
                              // disabled={!isCheckEditable}
                              // shouldClearable={isCheckEditable}
                            />
                          </Box>
                        </Box>

                        <Box className={classes.formFieldFull}>
                          <FormTextField name='mailingZipCode' label={`Zip Code${isCheckFieldsMandatory ? ' *' : ''}`}  />
                        </Box>

                        <FormMaskedTextField
                          name='phoneNumber'
                          label={`Phone number ${isCheckFieldsMandatory ? ' *' : ''}`}
                          // disabled={!isCheckEditable}
                          mask='+19999999999'
                        />

                      </>)

                    })()}

                    {/* Contact Information Section */}
                    <Box className={classes.divider} />
                    <Typography className={classes.formDetails}>
                      Contact Information
                    </Typography>

                    {(() => {
                      return (<>
                        <Box className={classes.formFieldFull}>
                          <FormTextField name='contactName' label={`Person to contact${isParticipationAddressActive ? ' *' : ''}`} />
                        </Box>
                        <Box className={classes.formFieldFull}>
                          <FormTextField name='email' label={`Email${isParticipationAddressActive ? ' *' : ''}`} />
                        </Box>
                      </>)
                    })()}

                    {/* Participation Address Section */}
                    <Box className={classes.divider} />
                    <Typography className={classes.formDetails}>
                      Registered Address
                    </Typography>

                    {(() => {
                      // const provisioningStatus = data?.mailing?.provisioningStatus;
                      // const isCheckEditable = !data?.mailing || provisioningStatus === 'NotStarted' || provisioningStatus === 'Error';

                      return (<>
                        <Box mt='4px' display='flex' justifyContent='space-between'>


                          {isParticipationAddressVerified && !participationAddressChanged ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#4CAF50',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: 'Inter',
                                padding: '5px'
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: '16px' }} />
                              <span>Address verification completed</span>

                            </Box>
                          ) : hasParticipationAddress && isParticipationAddressKeptAsEntered && !participationAddressChanged ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#FF9800',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: 'Inter',
                                padding: '5px'
                              }}
                            >
                              <ErrorIcon sx={{ fontSize: '16px' }} />
                              <span>Address not verified by USPS</span>
                            </Box>

                          ) : (
                            participationAddress1 && participationCity && participationState && participationZip && (
                              <>  <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  color: '#FF9800',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  fontFamily: 'Inter',
                                  padding: '5px'
                                }}
                              >
                                <ErrorIcon sx={{ fontSize: '16px' }} />
                                <span>Address not verified. Please verify</span>
                              </Box>

                                <Box
                                  onClick={handleVerifyParticipationAddress}
                                  className={classes.verifyButton}
                                >
                                  <span>Verify</span>
                                </Box>
                              </>
                            )
                          )}
                        </Box>

                        <Box>
                          <AddressAutocomplete
                            name='participationAddress1'
                            label='Address Line 1'
                            optionsQuery={addressOptionsQueryVendor}
                            mapQueryResults={mapAddressResultsVendor}
                            onValueChange={handleParticipationAddressSelect}
                            isDisableClearable={false}
                            addressType='Registered Address'
                            InputProps={{
                              value: participationAddress1 || '',
                              placeholder: 'Enter participation address line 1'
                            }}
                          />
                        </Box>

                        <Box>
                          <FormTextField name='participationAddress2' label='Address Line 2' />
                        </Box>

                        <Box className={classes.formFieldsRow}>
                          <Box className={classes.formFieldFull}>
                            <FormTextField name='participationCity' label='City' />
                          </Box>
                          <Box className={classes.formFieldFull}>
                            <FormSelectField
                              name='participationState'
                              label='State'
                              options={US_STATES_LIST}
                              width='100%'
                              shouldClearable={true}
                            />
                          </Box>
                        </Box>

                        <Box className={classes.formFieldFull}>
                          <FormTextField name='participationZip' label='Zip Code' />
                        </Box>
                      </>)

                    })()}

                  </Box>



                ) : (
                  // Read-Only Mode - Show Clean Text Display
                  <Box className={classes.readOnlySection}>
                    {/* Account Details Section */}
                    <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                      <Typography className={classes.formDetails}>
                        ACH
                      </Typography>
                      <Box display='flex' alignItems='center' gap='8px'>
                        <Typography sx={{ color: '#ffffff', fontSize: '12px' }}>Default Method</Typography>
                        <Radio
                          checked={formValues.defaultPaymentMethod === 'ACH'}
                          sx={{
                            color: '#5e6174',
                            '&.Mui-checked': {
                              color: '#2DD4BF',
                            },
                            padding: '4px',
                          }}
                          size='small'
                          disabled
                        />
                      </Box>
                    </Box>

                    <Box className={classes.readOnlyRow}>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Name</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.name || '-'}</Typography>
                      </Box>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Account Type</Typography>
                        <Typography className={classes.readOnlyValue}>{getAccountTypeLabel(formValues.accountType || '-')}</Typography>
                      </Box>
                    </Box>

                    <Box className={classes.readOnlyRow}>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Account No.</Typography>

                        <Box display='flex' alignItems='center' gap='8px'>
                          <Typography className={classes.readOnlyValue}>
                            {formValues.accountNumber ? (isAccountNumberVisibleInReadOnly
                              ? fullAccountNumber || formValues.accountNumber
                              : fullAccountNumber
                                ? maskedAccountNumber
                                : formValues.accountNumber || '************') : '-'}
                          </Typography>

                          {(formValues.accountNumber || fullAccountNumber) && (
                            <Box
                              onClick={!isAccountNumberLoading ? getAccountNumber : undefined}
                              sx={{
                                cursor: isAccountNumberLoading ? 'not-allowed' : 'pointer',
                                color: isAccountNumberLoading ? 'text.disabled' : 'info.main',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              {isAccountNumberLoading ? (
                                <CircularProgress size={16} thickness={5} />
                              ) : isAccountNumberVisibleInReadOnly ? (
                                <VisibilityOffIcon fontSize='small' />
                              ) : (
                                <VisibilityIcon fontSize='small' />
                              )}
                            </Box>
                          )}

                        </Box>
                      </Box>


                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Routing No.</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.routingNumber || '-'}</Typography>
                      </Box>

                    </Box>

                    {/* Mailing Address Section */}
                    <Box className={classes.divider} />
                    <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                      <Typography className={classes.formDetails}>
                        CHECK
                      </Typography>
                      <Box display='flex' alignItems='center' gap='8px'>
                        <Typography sx={{ color: '#ffffff', fontSize: '12px' }}>Default Method</Typography>
                        <Radio
                          checked={formValues.defaultPaymentMethod === 'CHECK'}
                          sx={{
                            color: '#5e6174',
                            '&.Mui-checked': {
                              color: '#2DD4BF',
                            },
                            padding: '4px',
                          }}
                          size='small'
                          disabled
                        />
                      </Box>
                    </Box>
                    <Box display='flex' justifyContent='space-between' >
                      {isMailingAddressVerified && !mailingAddressChanged ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#4CAF50',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: 'Inter',
                            padding: '5px'
                          }}
                        >
                          <CheckCircleIcon sx={{ fontSize: '16px' }} />
                          <span>Address verification completed</span>
                        </Box>
                      ) : isMailingAddressKeptAsEntered && !mailingAddressChanged ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#FF9800',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: 'Inter',
                            padding: '5px'
                          }}
                        >
                          <ErrorIcon sx={{ fontSize: '16px' }} />
                          <span>Address not verified by USPS</span>
                        </Box>
                      ) : null}
                    </Box>

                    <Box className={classes.readOnlyField}>
                      <Typography className={classes.readOnlyLabel}>Address Line 1</Typography>
                      <Typography className={classes.readOnlyValue}>{formValues.mailingAddress1 || '-'}</Typography>
                    </Box>

                    {formValues.mailingAddress2 && (
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Address Line 2</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.mailingAddress2 || '-'}</Typography>
                      </Box>

                    )}

                    <Box className={classes.readOnlyRow}>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>City</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.mailingCity || '-'}</Typography>
                      </Box>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>State</Typography>
                        <Typography className={classes.readOnlyValue}>{getStateLabel(formValues.mailingState || '-')}</Typography>
                      </Box>
                    </Box>

                    <Box className={classes.readOnlyRow}>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Zip Code</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.mailingZipCode || '-'}</Typography>
                      </Box>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Phone Number</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.phoneNumber || '-'}</Typography>
                      </Box>
                    </Box>

                    <Box className={classes.divider} />
                    {/* Contact Information Section */}
                    <Typography className={classes.formDetails}>
                      Contact Information
                    </Typography>
                    <Box className={classes.readOnlyRow}>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Contact Name</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.contactName || '-'}</Typography>
                      </Box>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Email</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.email || '-'}</Typography>
                      </Box>
                    </Box>

                    <Box className={classes.divider} />
                    {/* Participation Address Section */}
                    <Typography className={classes.formDetails}>
                      Registered Address
                    </Typography>

                    <Box display='flex' justifyContent='space-between' >

                      {isParticipationAddressVerified ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#4CAF50',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: 'Inter',
                            padding: '5px'
                          }}
                        >
                          <CheckCircleIcon sx={{ fontSize: '16px' }} />
                          <span>Address verification completed</span>
                        </Box>
                      ) : hasParticipationAddress && isParticipationAddressKeptAsEntered ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#FF9800',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: 'Inter',
                            padding: '5px'
                          }}
                        >
                          <ErrorIcon sx={{ fontSize: '16px' }} />
                          <span>Address not verified by USPS</span>
                        </Box>
                      ) : null}
                    </Box>

                    <Box className={classes.readOnlyField}>
                      <Typography className={classes.readOnlyLabel}>Address Line 1</Typography>
                      <Typography className={classes.readOnlyValue}>{formValues.participationAddress1 || '-'}</Typography>
                    </Box>

                    {formValues.participationAddress2 && (
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Address Line 2</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.participationAddress2 || '-'}</Typography>
                      </Box>

                    )}

                    <Box className={classes.readOnlyRow}>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>City</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.participationCity || '-'}</Typography>
                      </Box>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>State</Typography>
                        <Typography className={classes.readOnlyValue}>{getStateLabel(formValues.participationState || '-')}</Typography>
                      </Box>
                    </Box>

                    <Box className={classes.readOnlyRow}>
                      <Box className={classes.readOnlyField}>
                        <Typography className={classes.readOnlyLabel}>Zip Code</Typography>
                        <Typography className={classes.readOnlyValue}>{formValues.participationZip || '-'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* Consent & Authorization Section */}
            {!isEditing && !showThankYouCard && !showAlreadyConfirmedModal && !hasConfirmedBefore && (
              <Box className={classes.divider} sx={{ marginTop: '32px', marginBottom: '8px' }} />
            )}
            {!isEditing && !showThankYouCard && !showAlreadyConfirmedModal && !hasConfirmedBefore && (
              <Box mt="40px">
                <ConsentAuthorizationSection
                  authorizationMessage={VENDOR_BANKING_AUTHORIZATION_MESSAGE}
                  isAgreed={isConsentAgreed}
                  onAgreementChange={setIsConsentAgreed}
                  onTermsClick={handleViewTermsAndConditions}
                />
              </Box>
            )}

            {/* Buttons */}
            {!showThankYouCard && !showAlreadyConfirmedModal && (
              <Box className={classes.buttonsContainer} sx={{ marginTop: isEditing ? '32px' : '40px' }}>
                {isEditing ? (
                  <>
                    <FormSubmitButton
                      disabled={
                        isFormSubmitting ||
                        !isDirty ||
                        !isValid ||
                        isMailingAddressNeedsVerification ||
                        isParticipationAddressNeedsVerification ||
                        (isAchFieldsMandatory && (!accountType || !accountNumber || !routingNumber)) ||
                        (isCheckFieldsMandatory && (!mailingAddress1 || !mailingCity || !mailingState || !mailingZipCode || !phoneNumber))
                      }
                      buttonText='Save'
                      onClick={handleSubmit(onSubmitForm)}
                    />
                    <FormCancelButton onClick={handleCancel} buttonText='Cancel' />
                  </>
                ) : (
                  <>
                    <CommonButton
                      type='infoMain'
                      buttonText='Confirm Details'
                      onButtonClick={() => onConfirm(formValues as BankingFormData)}
                      disabled={isFormSubmitting || isError || (!hasConfirmedBefore && !isConsentAgreed)}
                    />
                    <FormCancelButton onClick={handleEdit} buttonText='Edit' disabled={isError} />
                  </>
                )}
              </Box>
            )}
          </form>
        </FormProvider>

        {/* Address Verification Popup */}
        {showAddressVerification && (
          <VendorConfirmationPopupBanking
            open={showAddressVerification}
            addressType='Banking Address'
            enteredAddress={`${mailingAddress1}, ${mailingAddress2 ? mailingAddress2 + ', ' : ''}${mailingCity}, ${mailingState} ${mailingZipCode}`}
            onConfirm={handleVendorConfirmation}
            onCancel={() => setShowAddressVerification(false)}
            onVendorSelect={handleVendorOptionSelect}
          />
        )}

        {/* Participation Address Verification Popup */}
        {showParticipationAddressVerification && (
          <VendorConfirmationPopupBanking
            open={showParticipationAddressVerification}
            addressType='Registered Address'
            enteredAddress={`${participationAddress1}, ${participationAddress2 ? participationAddress2 + ', ' : ''}${participationCity}, ${participationState} ${participationZip}`}
            onConfirm={handleParticipationVendorConfirmation}
            onCancel={() => setShowParticipationAddressVerification(false)}
            onVendorSelect={handleParticipationVendorOptionSelect}
          />
        )}
      </Box>
    </Box>
  );
};

export default ConfirmBankingView;